import { useState } from "react";
import { Input, Modal } from "antd";
import { BiChevronLeft } from "react-icons/bi";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router";
import { escalateAlert, getAlert, getMonitoringSettings, rejectHold, releaseHold, updateAlert } from "../../api/monitoring";
import { showErrorToast, showSuccessToast } from "../../utils/toast";
import { usePermissionEffect } from "../../utils/usePermissionEffect";
import CustomerLink from "../shared/CustomerLink";
import { HOLD_LABELS, Pill, STATUS_LABELS, SeverityBadge, TIER_LABELS, dueText, naira } from "./shared";

const btn = "rounded-[5px] px-4 py-2 font-bold text-[13px] disabled:opacity-50 whitespace-nowrap";

const fmtValue = (k, v) => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  if (/ngn|amount|total/i.test(k) && typeof v === "number") return naira(v);
  return String(v);
};

const AlertDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getModulePermission } = usePermissionEffect();
  const canWork = getModulePermission("monitoring", "canUpdate");
  const [note, setNote] = useState("");

  const { data: a } = useQuery(["monitoring-alert", id], () => getAlert(id), { refetchInterval: 20000 });
  const { data: settings } = useQuery("monitoring-settings", getMonitoringSettings);

  const done = (msg) => () => {
    showSuccessToast(msg);
    queryClient.invalidateQueries(["monitoring-alert", id]);
    queryClient.invalidateQueries("monitoring-alerts");
    queryClient.invalidateQueries("monitoring-dashboard");
  };
  const onError = (e) => showErrorToast(e.message);

  const update = useMutation(updateAlert, { onSuccess: done("Alert updated"), onError });
  const release = useMutation(releaseHold, { onSuccess: (r) => done(r?.message || "Payment released")(), onError });
  const reject = useMutation(rejectHold, { onSuccess: done("Payment rejected and refunded"), onError });
  const escalate = useMutation(escalateAlert, {
    onSuccess: (c) => {
      done("Escalated to a case")();
      navigate(`/monitoring/cases/${c._id}`);
    },
    onError,
  });

  if (!a) return <div className="text-[14px] text-[#00000080]">Loading…</div>;

  const active = ["open", "in_review"].includes(a.status);
  const held = a.hold?.state === "held";
  const due = active ? dueText(a.dueAt) : null;
  const autoAt = held && settings ? new Date(new Date(a.createdAt).getTime() + settings.holdTimeoutMinutes * 60000) : null;

  const askReason = (title, okText, onOk, danger) => {
    let reason = "";
    Modal.confirm({
      title,
      okText,
      okButtonProps: { danger },
      content: <Input.TextArea rows={3} placeholder="Reason (kept in the audit log)" onChange={(e) => (reason = e.target.value)} />,
      onOk: () => {
        if (!reason.trim()) {
          showErrorToast("A reason is required");
          return Promise.reject();
        }
        return onOk(reason.trim());
      },
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <BiChevronLeft className="text-[25px] cursor-pointer" onClick={() => navigate(-1)} />
        <SeverityBadge severity={a.severity} />
        <div className="font-bold text-[18px]">{a.ruleName}</div>
        <Pill className="bg-[#0000000D] text-[#4F4F4F]">{STATUS_LABELS[a.status]}</Pill>
        {due && <span className={`text-[13px] ${due.late ? "text-[#EB5757]" : "text-[#00000080]"}`}>{due.text}</span>}
      </div>

      <div className="flex gap-4 items-start">
        <div className="w-2/3 space-y-4">
          {a.hold?.state && (
            <div className={`rounded-lg p-5 ${held ? "bg-[#EB57571A] border border-[#EB5757]" : "bg-white"}`}>
              <div className="font-bold text-[15px]">
                {HOLD_LABELS[a.hold.state]}: {a.hold.kind === "bank_payout" ? `bank withdrawal of ${naira(a.hold.amount)}` : `${a.hold.amount} ${String(a.hold.coin || "").toUpperCase()} crypto send`}
              </div>
              {held ? (
                <div className="text-[13px] mt-1">
                  The customer has been debited; the payment won't be sent until you decide.
                  {autoAt && settings.onHoldTimeout !== "keep" && (
                    <>
                      {" "}If nobody acts, it will be <b>{settings.onHoldTimeout === "release" ? "released" : "rejected and refunded"}</b> automatically at{" "}
                      {autoAt.toLocaleString()}.
                    </>
                  )}
                </div>
              ) : (
                <div className="text-[13px] mt-1 text-[#00000099]">
                  {a.hold.resolvedBy === "auto" ? "Automatically" : `By ${a.hold.resolvedBy}`} on{" "}
                  {a.hold.resolvedAt ? new Date(a.hold.resolvedAt).toLocaleString() : "—"}
                  {a.hold.reason ? ` — ${a.hold.reason}` : ""}
                </div>
              )}
              {held && canWork && (
                <div className="flex gap-3 mt-3">
                  <button
                    type="button"
                    disabled={release.isLoading || reject.isLoading}
                    className={`bg-dark text-white ${btn}`}
                    onClick={() =>
                      Modal.confirm({ title: "Release this payment?", content: "It will be sent to the customer's destination now.", okText: "Release", onOk: () => release.mutateAsync(id) })
                    }
                  >
                    Release payment
                  </button>
                  <button
                    type="button"
                    disabled={release.isLoading || reject.isLoading}
                    className={`text-[#EB5757] bg-white border border-[#EB5757] ${btn}`}
                    onClick={() => askReason("Reject and refund this payment?", "Reject & refund", (reason) => reject.mutateAsync({ id, reason }), true)}
                  >
                    Reject & refund
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="bg-white rounded-lg p-5 space-y-3">
            <div className="font-bold text-[14px]">What happened</div>
            <div className="text-[15px]">{a.summary}</div>
            <div className="grid grid-cols-3 gap-3 text-[13px]">
              {Object.entries(a.data || {})
                .filter(([k]) => k !== "params")
                .map(([k, v]) => (
                  <div key={k}>
                    <div className="text-[12px] text-[#00000080]">{k}</div>
                    <div className="break-all">{fmtValue(k, v)}</div>
                  </div>
                ))}
            </div>
            {a.data?.params && (
              <div className="text-[12px] text-[#00000080]">
                Rule settings at the time ({TIER_LABELS[a.tier] || a.tier}):{" "}
                {Object.entries(a.data.params).map(([k, v]) => `${k} ${v}`).join(" · ")}
              </div>
            )}
            {a.transactionRefs?.length > 0 && (
              <div className="text-[12px]">
                <span className="text-[#00000080]">Transactions: </span>
                <span className="font-mono break-all">{a.transactionRefs.join(", ")}</span>
              </div>
            )}
            <div className="text-[12px] text-[#00000080]">
              Raised {new Date(a.createdAt).toLocaleString()} · {a.stage === "pre" ? "checked before the transaction ran" : "pattern found after the fact"}
              {a.outcome ? ` · transaction ${a.outcome}` : ""}
            </div>
          </div>

          <div className="bg-white rounded-lg p-5 space-y-3">
            <div className="font-bold text-[14px]">Notes</div>
            {(a.notes || []).map((n, i) => (
              <div key={i} className="text-[13px] border-b border-[#F2F2F2] pb-2">
                <div>{n.text}</div>
                <div className="text-[12px] text-[#00000080]">
                  {n.by} · {new Date(n.at).toLocaleString()}
                </div>
              </div>
            ))}
            {a.resolution && (
              <div className="text-[13px]">
                <b>{STATUS_LABELS[a.status]}:</b> {a.resolution} <span className="text-[#00000080]">({a.resolvedBy})</span>
              </div>
            )}
            {canWork && (
              <div className="flex gap-2">
                <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note" />
                <button
                  type="button"
                  disabled={!note.trim() || update.isLoading}
                  className={`bg-[#0000000D] ${btn}`}
                  onClick={() => update.mutate({ id, note }, { onSuccess: () => setNote("") })}
                >
                  Add
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="w-1/3 space-y-4">
          <div className="bg-white rounded-lg p-5 space-y-2">
            <div className="font-bold text-[14px]">Customer</div>
            <CustomerLink userId={a.userId} record={{ user: a.customer }} />
            <div className="text-[13px] text-[#00000080]">{a.customer?.email}</div>
            <div className="text-[13px] text-[#00000080]">{a.customer?.phoneNumber}</div>
            <div className="text-[13px]">{TIER_LABELS[a.tier] || "—"}</div>
            {a.case && (
              <div className="text-[13px] pt-2">
                Case:{" "}
                <button type="button" className="text-[#4A4DE6]" onClick={() => navigate(`/monitoring/cases/${a.caseId}`)}>
                  {a.case.title}
                </button>{" "}
                ({a.case.status})
              </div>
            )}
          </div>

          {canWork && active && (
            <div className="bg-white rounded-lg p-5 space-y-2">
              <div className="font-bold text-[14px]">Actions</div>
              <button type="button" className={`w-full bg-[#0000000D] ${btn}`} onClick={() => update.mutate({ id, assignee: "me", status: "in_review" })}>
                {a.assignee ? `Assigned to ${a.assignee} – take over` : "Assign to me & start review"}
              </button>
              <button
                type="button"
                disabled={held}
                title={held ? "Release or reject the payment first" : ""}
                className={`w-full bg-[#0000000D] ${btn}`}
                onClick={() => askReason("Dismiss this alert?", "Dismiss", (resolution) => update.mutateAsync({ id, status: "dismissed", resolution }))}
              >
                Dismiss (false alarm)
              </button>
              <button
                type="button"
                disabled={held}
                className={`w-full bg-[#0000000D] ${btn}`}
                onClick={() => askReason("Resolve this alert?", "Resolve", (resolution) => update.mutateAsync({ id, status: "resolved", resolution }))}
              >
                Resolve
              </button>
              <button type="button" className={`w-full bg-dark text-white ${btn}`} onClick={() => escalate.mutate({ id })}>
                Escalate to case
              </button>
            </div>
          )}

          {a.related?.length > 0 && (
            <div className="bg-white rounded-lg p-5 space-y-2">
              <div className="font-bold text-[14px]">Other alerts for this customer</div>
              {a.related.map((r) => (
                <div key={r._id} className="text-[13px] cursor-pointer hover:underline" onClick={() => navigate(`/monitoring/alerts/${r._id}`)}>
                  <SeverityBadge severity={r.severity} /> {r.ruleName} · {STATUS_LABELS[r.status]}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AlertDetail;
